import React, { useEffect, useState, useMemo, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Flame, AlertTriangle, ListChecks, FileText, Filter, Download,
  ChevronDown, ChevronRight, CheckSquare, Square, Users, Library, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AppealEditorModal from "@/components/AppealEditorModal";
import { Link } from "react-router-dom";

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 }).format(n || 0);
const fmtMoneyFull = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("en-US").format(n || 0);

// Traffic-light priorities — readable on both light and dark themes.
// High = red, Medium = amber, Low = green; Critical = deeper red.
const PRIORITY_STYLES = {
  Critical: { bg: "rgba(220,38,38,0.22)",  border: "rgba(220,38,38,0.65)",  color: "#FCA5A5", icon: Flame },
  High:     { bg: "rgba(248,113,113,0.20)", border: "rgba(248,113,113,0.60)", color: "#FCA5A5", icon: AlertTriangle },
  Medium:   { bg: "rgba(251,191,36,0.20)",  border: "rgba(251,191,36,0.60)",  color: "#FCD34D", icon: ListChecks },
  Low:      { bg: "rgba(74,222,128,0.20)",  border: "rgba(74,222,128,0.60)",  color: "#86EFAC", icon: ListChecks },
};

// Back-compat alias (used by the section header card to pick an icon).
const PRIORITY_COLORS = PRIORITY_STYLES;

const STATUS_OPTIONS = ["open", "in_review", "appealed", "paid", "written_off"];

const Pill = ({ value, palette }) => {
  const m = palette[value] || palette.default;
  // Inline-style variant (preferred — used by traffic-light priority pills).
  if (m && m.color) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider"
        style={{ background: m.bg, border: `1px solid ${m.border}`, color: m.color }}
      >
        {value}
      </span>
    );
  }
  // Fallback for older Tailwind-class palettes (status pills, etc).
  const fallback = m || { bg: "bg-slate-100", border: "border-slate-200", text: "text-slate-700" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${fallback.bg} ${fallback.border} ${fallback.text}`}>
      {value}
    </span>
  );
};

const STATUS_PALETTE = {
  open:        { bg: "bg-slate-100",   border: "border-slate-300",   text: "text-slate-700" },
  in_review:   { bg: "bg-sky-100",     border: "border-sky-300",     text: "text-sky-800" },
  appealed:    { bg: "bg-indigo-100",  border: "border-indigo-300",  text: "text-indigo-800" },
  paid:        { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-800" },
  written_off: { bg: "bg-slate-200",   border: "border-slate-400",   text: "text-slate-700" },
};

export default function WorkQueuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    buckets: [], payers: [], priorities: [], statuses: [],
    appeal_recommended: null, min_amount: "", max_amount: "", min_aging: "", max_aging: "",
  });
  const [expandedBuckets, setExpandedBuckets] = useState({});
  const [expandedRows, setExpandedRows] = useState({}); // account_id -> bool
  const toggleRow = (id) => setExpandedRows((p) => ({ ...p, [id]: !p[id] }));
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editorBucketId, setEditorBucketId] = useState(null);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.buckets.length) params.set("buckets", filters.buckets.join(","));
    if (filters.payers.length) params.set("payers", filters.payers.join(","));
    if (filters.priorities.length) params.set("priorities", filters.priorities.join(","));
    if (filters.statuses.length) params.set("statuses", filters.statuses.join(","));
    if (filters.appeal_recommended !== null) params.set("appeal_recommended", String(filters.appeal_recommended));
    if (filters.min_amount !== "") params.set("min_amount", filters.min_amount);
    if (filters.max_amount !== "") params.set("max_amount", filters.max_amount);
    if (filters.min_aging !== "") params.set("min_aging", filters.min_aging);
    if (filters.max_aging !== "") params.set("max_aging", filters.max_aging);
    params.set("limit", "500");
    return params.toString();
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = buildQuery();
      const { data } = await api.get(`/workqueue/accounts${q ? `?${q}` : ""}`);
      setData(data);
      setSelected(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load work queue");
    } finally { setLoading(false); }
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleArrayFilter = (key, value) => {
    setFilters((f) => {
      const arr = f[key];
      return { ...f, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const clearFilters = () => setFilters({
    buckets: [], payers: [], priorities: [], statuses: [],
    appeal_recommended: null, min_amount: "", max_amount: "", min_aging: "", max_aging: "",
  });

  const toggleBucket = (driver) => setExpandedBuckets((b) => ({ ...b, [driver]: !b[driver] }));

  const accounts = data?.accounts || [];
  const summaries = data?.summaries || [];

  // Group accounts by driver (post-filter)
  const accountsByDriver = useMemo(() => {
    const m = {};
    for (const a of accounts) {
      (m[a.denial_driver] ||= []).push(a);
    }
    return m;
  }, [accounts]);

  const toggleSelect = (id) => setSelected((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const selectAllInBucket = (driver) => {
    const ids = (accountsByDriver[driver] || []).map((a) => a.account_id);
    setSelected((s) => {
      const next = new Set(s);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const bulkUpdate = async (status) => {
    if (selected.size === 0) { toast.error("Select at least one account"); return; }
    setBulkBusy(true);
    try {
      const { data: resp } = await api.post("/workqueue/accounts/bulk-status", {
        claim_ids: Array.from(selected),
        status,
      });
      toast.success(`Updated ${resp.updated} accounts → ${status.replace("_", " ")}`);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally { setBulkBusy(false); }
  };

  const generateBatchAppeal = async () => {
    if (selected.size === 0) { toast.error("Select at least one account"); return; }
    if (selected.size > 50) { toast.error("Batch limit is 50 claims at once."); return; }
    setBulkBusy(true);
    try {
      const resp = await api.post(
        "/workqueue/accounts/batch-appeal",
        { claim_ids: Array.from(selected) },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `batch_appeal_${selected.size}_claims.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Generated combined appeal packet for ${selected.size} claims`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Batch appeal failed");
    } finally { setBulkBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const q = buildQuery();
      const resp = await api.get(`/workqueue/accounts/export${q ? `?${q}` : ""}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rciq_workqueue_accounts.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported work queue CSV");
    } catch (err) {
      toast.error("CSV export failed");
    }
  };

  const generatePack = async (driver) => {
    // Open the editable AI-draft modal (instead of immediately downloading the PDF)
    const s = summaries.find((s) => s.driver === driver);
    if (!s) return;
    const top = (accountsByDriver[driver] || [])[0];
    if (!top) return;
    const bucketId = `${top.payer.replace(/\s+/g, "_")}__${driver.replace(/\s+/g, "_")}`;
    setEditorBucketId(bucketId);
  };

  if (loading && !data) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center py-32 text-slate-500">
          <Loader2 className="h-10 w-10 animate-spin text-[#0F4C81]" />
          <div className="mt-4 text-sm">Loading per-account work queue...</div>
        </div>
      </AppShell>
    );
  }

  if (!data?.has_data) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto text-center py-20">
          <h2 className="font-display text-2xl font-semibold text-slate-900">Your work queue is clear</h2>
          <p className="text-slate-500 mt-2">No open denials in the active dataset. Load a dataset to populate the queue.</p>
        </div>
      </AppShell>
    );
  }

  const opts = data.filter_options;
  const totalDollars = accounts.reduce((s, a) => s + a.denied_amount, 0);

  return (
    <AppShell>
      <div className="space-y-6 fade-up" data-testid="workqueue-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="label-overline">Denial Operations</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1.5 tracking-tight">Account-Level Work Queue</h1>
            <p className="text-sm text-slate-500 mt-1">
              <strong className="text-slate-700">{fmtNum(accounts.length)}</strong> accounts · <strong className="text-slate-700">{fmtMoneyFull(totalDollars)}</strong> at risk · {summaries.length} operational buckets
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/ansi-codes" data-testid="wq-ansi-library-link"
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-teal-700 hover:text-teal-600 px-3 py-2 rounded-md border border-slate-300 hover:border-teal-400 transition-colors">
              <Library className="h-3.5 w-3.5" />ANSI Library
            </Link>
            <Button variant="outline" size="sm" data-testid="wq-export-csv" onClick={exportCsv} className="border-slate-300 rounded-lg">
              <Download className="h-3.5 w-3.5 mr-1.5" />Export CSV
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="surface-card p-4" data-testid="wq-filter-bar">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Filters</span>
            <button data-testid="wq-clear-all" onClick={clearFilters} className="ml-auto text-xs text-[#0F4C81] hover:underline">Clear all</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            <FilterSelect label="Bucket" testId="filter-buckets" options={opts.buckets} selected={filters.buckets} onToggle={(v) => toggleArrayFilter("buckets", v)} />
            <FilterSelect label="Payer" testId="filter-payers" options={opts.payers} selected={filters.payers} onToggle={(v) => toggleArrayFilter("payers", v)} />
            <FilterSelect label="Priority" testId="filter-priorities" options={opts.priorities} selected={filters.priorities} onToggle={(v) => toggleArrayFilter("priorities", v)} />
            <FilterSelect label="Status" testId="filter-statuses" options={opts.statuses} selected={filters.statuses} onToggle={(v) => toggleArrayFilter("statuses", v)} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs mt-3">
            <div data-testid="filter-appeal">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Appeal Recommended</label>
              <select value={filters.appeal_recommended === null ? "" : String(filters.appeal_recommended)} onChange={(e) => setFilters((f) => ({ ...f, appeal_recommended: e.target.value === "" ? null : e.target.value === "true" }))} className="w-full border border-slate-300 rounded-md px-2 py-1.5 bg-white">
                <option value="">Any</option><option value="true">Yes</option><option value="false">No</option>
              </select>
            </div>
            <NumberFilter label="Min $" testId="filter-min-amount" value={filters.min_amount} onChange={(v) => setFilters((f) => ({ ...f, min_amount: v }))} />
            <NumberFilter label="Max $" testId="filter-max-amount" value={filters.max_amount} onChange={(v) => setFilters((f) => ({ ...f, max_amount: v }))} />
            <NumberFilter label="Min Aging Days" testId="filter-min-aging" value={filters.min_aging} onChange={(v) => setFilters((f) => ({ ...f, min_aging: v }))} />
            <NumberFilter label="Max Aging Days" testId="filter-max-aging" value={filters.max_aging} onChange={(v) => setFilters((f) => ({ ...f, max_aging: v }))} />
          </div>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div data-testid="wq-bulk-bar" className="rounded-xl bg-navy text-white px-4 py-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-cyan-300" />
              <span><strong>{selected.size}</strong> account{selected.size === 1 ? "" : "s"} selected</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                data-testid="wq-bulk-batch-appeal"
                disabled={bulkBusy || selected.size > 50}
                onClick={generateBatchAppeal}
                className="bg-teal-500 hover:bg-teal-400 text-slate-900 rounded-md h-7 px-3 text-xs font-bold uppercase tracking-wider"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                Generate Combined Appeal Packet
              </Button>
              {STATUS_OPTIONS.map((s) => (
                <Button key={s} size="sm" variant="outline" data-testid={`wq-bulk-${s}`} disabled={bulkBusy} onClick={() => bulkUpdate(s)} className="border-white/30 text-white hover:bg-white/10 rounded-md h-7 px-2.5 text-xs">
                  Mark {s.replace("_", " ")}
                </Button>
              ))}
              <button data-testid="wq-bulk-clear" onClick={() => setSelected(new Set())} className="text-xs text-cyan-200 hover:underline ml-1">Clear selection</button>
            </div>
          </div>
        )}

        {/* Bucket summaries with per-account drill-down */}
        <div className="space-y-3" data-testid="wq-buckets-list">
          {summaries.map((s) => {
            const driverAccounts = accountsByDriver[s.driver] || [];
            const expanded = !!expandedBuckets[s.driver];
            const meta = PRIORITY_COLORS[s.priority] || PRIORITY_COLORS.Low;
            const bucketSelected = driverAccounts.length > 0 && driverAccounts.every((a) => selected.has(a.account_id));

            return (
              <div key={s.driver} className="surface-card overflow-hidden" data-testid={`wq-bucket-${s.bucket_key || s.driver.replace(/\s+/g, "_")}`}>
                <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer hover:bg-slate-50/40 border-b border-slate-100" onClick={() => toggleBucket(s.driver)}>
                  <button data-testid={`wq-bucket-toggle-${s.driver.replace(/\s+/g, "_")}`} className="flex items-center gap-2 text-left flex-1">
                    {expanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-display text-lg font-semibold text-slate-900">{s.driver}</span>
                        <Pill value={s.priority} palette={PRIORITY_STYLES} />
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        <span className="font-medium text-slate-700">{driverAccounts.length} of {s.total_accounts}</span> accounts shown · {fmtMoneyFull(s.total_dollars)} total · top payer: <span className="font-medium text-slate-700">{s.top_payer}</span>
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 sm:ml-auto" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" data-testid={`wq-pack-${s.driver.replace(/\s+/g, "_")}`} onClick={() => generatePack(s.driver)} className="ai-cta rounded-md h-7 px-3 text-xs">
                      <FileText className="h-3 w-3 mr-1" />Generate Appeal Packet
                    </Button>
                    {driverAccounts.length > 0 && (
                      <Button size="sm" variant="ghost" data-testid={`wq-bucket-selectall-${s.driver.replace(/\s+/g, "_")}`} onClick={() => selectAllInBucket(s.driver)} className="h-7 px-2 text-xs">
                        {bucketSelected ? <CheckSquare className="h-3.5 w-3.5 mr-1 text-[#0F4C81]" /> : <Square className="h-3.5 w-3.5 mr-1 text-slate-400" />}
                        Select all
                      </Button>
                    )}
                  </div>
                </div>

                <div className="bg-slate-50/60 px-5 py-3 text-xs border-b border-slate-100">
                  <div><span className="font-semibold text-slate-700">Common root cause: </span>{s.common_root_cause}</div>
                  <div className="mt-1"><span className="font-semibold text-slate-700">Overall strategy: </span>{s.overall_strategy}</div>
                  {s.top_codes?.length > 0 && (
                    <div className="mt-1 text-slate-500">Top codes: {s.top_codes.map((c) => `${c.code}×${c.count}`).join(" · ")}</div>
                  )}
                </div>

                {expanded && (
                  <div className="overflow-x-auto" data-testid={`wq-accounts-table-${s.driver.replace(/\s+/g, "_")}`}>
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          <th className="py-2.5 px-3 w-8"></th>
                          <th className="py-2.5 px-3">Account</th>
                          <th className="py-2.5 px-3">Payer</th>
                          <th className="py-2.5 px-3">DOS</th>
                          <th className="py-2.5 px-3">Code</th>
                          <th className="py-2.5 px-3 text-right">$ at Risk</th>
                          <th className="py-2.5 px-3 text-right">Aging</th>
                          <th className="py-2.5 px-3">Priority</th>
                          <th className="py-2.5 px-3">Appeal?</th>
                          <th className="py-2.5 px-3">Status</th>
                          <th className="py-2.5 px-3">Due</th>
                          <th className="py-2.5 px-3">Next Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverAccounts.map((a, i) => {
                          const isExpanded = !!expandedRows[a.account_id];
                          const pb = a.next_action_detailed || null;
                          return (
                          <React.Fragment key={a.account_id}>
                          <tr data-testid={`wq-account-row-${a.account_id}`} className="border-b border-slate-100 hover:bg-slate-50/60 align-top">
                            <td className="py-3 px-3">
                              <button data-testid={`wq-select-${a.account_id}`} onClick={() => toggleSelect(a.account_id)}>
                                {selected.has(a.account_id)
                                  ? <CheckSquare className="h-4 w-4 text-[#0F4C81]" />
                                  : <Square className="h-4 w-4 text-slate-300 hover:text-slate-500" />}
                              </button>
                            </td>
                            <td className="py-3 px-3 font-mono text-slate-900 font-medium">{a.account_id}</td>
                            <td className="py-3 px-3 text-slate-700">{a.payer}</td>
                            <td className="py-3 px-3 text-slate-600">{a.dos}</td>
                            <td className="py-3 px-3">
                              {a.ansi_display ? (
                                <div className="flex flex-col gap-0.5 max-w-[160px]" title={a.ansi_description || a.denial_code}>
                                  <span
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold font-mono px-1.5 py-0.5 rounded bg-teal-50 text-teal-800 border border-teal-200 w-fit"
                                    data-testid={`wq-ansi-code-${a.account_id}`}
                                  >
                                    {a.ansi_display}
                                  </span>
                                  {a.ansi_short && (
                                    <span className="text-[10px] font-semibold text-slate-700 truncate" data-testid={`wq-ansi-short-${a.account_id}`}>
                                      {a.ansi_short}
                                    </span>
                                  )}
                                  {a.rarc_code && (
                                    <span className="text-[10px] font-mono text-slate-400">+ {a.rarc_code}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="font-mono text-slate-500">{a.denial_code}</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-right font-mono text-slate-900">{fmtMoneyFull(a.denied_amount)}</td>
                            <td className="py-3 px-3 text-right text-slate-600">{a.aging_days}d</td>
                            <td className="py-3 px-3">
                              <Pill value={a.priority} palette={{
                                Critical: { bg: "bg-red-100", border: "border-red-300", text: "text-red-800" },
                                High: { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-800" },
                                Medium: { bg: "bg-sky-100", border: "border-sky-300", text: "text-sky-800" },
                                Low: { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-700" },
                              }} />
                            </td>
                            <td className="py-3 px-3">
                              {a.appeal_recommended
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-100 border border-emerald-300 text-emerald-800">Yes · {a.recovery_likelihood}%</span>
                                : <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 border border-slate-300 text-slate-600">No</span>}
                            </td>
                            <td className="py-3 px-3"><Pill value={a.status} palette={STATUS_PALETTE} /></td>
                            <td className="py-3 px-3 text-slate-600">{a.due_date}</td>
                            <td className="py-3 px-3 text-slate-700 max-w-md">
                              <button
                                type="button"
                                onClick={() => toggleRow(a.account_id)}
                                data-testid={`wq-expand-${a.account_id}`}
                                className="text-left w-full group"
                                title={isExpanded ? "Hide appeal playbook" : "Show full appeal playbook"}
                              >
                                <div className="flex items-start gap-1.5">
                                  {isExpanded
                                    ? <ChevronDown className="h-3.5 w-3.5 text-teal-700 mt-0.5 flex-shrink-0" />
                                    : <ChevronRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-teal-700 mt-0.5 flex-shrink-0" />}
                                  <div data-testid={`wq-action-text-${a.account_id}`} className="group-hover:text-slate-900">{a.next_action}</div>
                                </div>
                                <div className="text-[10px] text-slate-500 mt-1 ml-5">
                                  {isExpanded ? "Hide fighting playbook" : "Click for full fighting playbook →"}
                                </div>
                              </button>
                            </td>
                          </tr>
                          {isExpanded && pb && (
                            <tr data-testid={`wq-playbook-${a.account_id}`} className="bg-gradient-to-br from-slate-50 via-white to-teal-50/30 border-b border-slate-200">
                              <td colSpan={12} className="px-6 py-5">
                                <PlaybookPanel pb={pb} accountId={a.account_id} payer={a.payer} amount={a.denied_amount} aging={a.aging_days} />
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );})}
                        {driverAccounts.length === 0 && (
                          <tr><td colSpan={12} className="py-5 text-center text-xs text-slate-500">No accounts match current filters in this bucket.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-xs text-slate-500">
          Showing first {accounts.length} of {data.total_accounts} matching accounts. Refine filters to narrow further.
        </div>
      </div>

      {editorBucketId && (
        <AppealEditorModal
          bucketId={editorBucketId}
          onClose={() => setEditorBucketId(null)}
        />
      )}
    </AppShell>
  );
}

// ---------- PlaybookPanel — clean single-column document layout for reading ----------
const PlaybookPanel = ({ pb, accountId, payer, amount, aging }) => {
  if (!pb) return null;
  const SectionHeading = ({ children }) => (
    <h3 className="text-[16px] font-bold text-slate-900 mt-7 mb-3 pb-1.5 border-b border-slate-200">
      {children}
    </h3>
  );
  return (
    <div
      className="max-w-[820px] mx-auto bg-white rounded-lg border border-slate-200 px-7 py-6 shadow-sm"
      data-testid={`wq-playbook-content-${accountId}`}
    >
      {/* Urgency / escalation banners — only when truly relevant */}
      {pb.urgency === "URGENT" && (
        <div
          className="rounded-md px-4 py-3 mb-5 bg-red-50 border-l-4 border-red-500 text-red-900 text-[14px]"
          data-testid={`wq-playbook-urgency-${accountId}`}
        >
          <span className="font-bold">URGENT.</span> {pb.urgency_note}
        </div>
      )}
      {pb.urgency === "HIGH" && (
        <div
          className="rounded-md px-4 py-3 mb-5 bg-amber-50 border-l-4 border-amber-500 text-amber-900 text-[14px]"
          data-testid={`wq-playbook-urgency-${accountId}`}
        >
          <span className="font-bold">High priority.</span> {pb.urgency_note}
        </div>
      )}
      {pb.escalation_required && (
        <div
          className="rounded-md px-4 py-3 mb-5 bg-slate-50 border-l-4 border-slate-400 text-slate-800 text-[14px]"
          data-testid={`wq-playbook-escalation-${accountId}`}
        >
          <span className="font-bold">Escalation required.</span> {pb.escalation_note}
        </div>
      )}

      {/* Headline action */}
      <div className="text-[18px] font-semibold text-slate-900 leading-snug mb-2">
        {pb.headline_action}
      </div>
      <div className="text-[13px] text-slate-500 mb-1">
        {payer} · ${Number(amount || 0).toLocaleString()} denied · {aging} days aged
      </div>

      {/* Claim-specific case context — opens the playbook with THIS claim's facts */}
      {pb.case_context && (
        <div
          className="mt-5 rounded-md bg-slate-50 border-l-4 border-slate-400 px-4 py-3 text-[15px] text-slate-800 leading-[1.65]"
          data-testid={`wq-playbook-case-context-${accountId}`}
        >
          {pb.case_context}
        </div>
      )}

      {/* Appeal argument — the most important section, full prose */}
      <SectionHeading>Why you can win this</SectionHeading>
      <p className="text-[15px] text-slate-800 leading-[1.65]">
        {pb.appeal_argument}
      </p>

      {/* Code-specific intelligence — inline note if present */}
      {pb.code_notes && pb.code_notes.length > 0 && (
        <div className="mt-4 rounded-md bg-teal-50/60 border border-teal-200 px-4 py-3 text-[14px] text-slate-800 leading-[1.6]">
          {pb.code_notes.map((n, i) => (
            <p key={i} className={i > 0 ? "mt-2" : ""}>{n}</p>
          ))}
        </div>
      )}

      {/* Documents to pull */}
      <SectionHeading>Documents to attach</SectionHeading>
      <ul className="space-y-2 text-[15px] text-slate-800 leading-[1.6]">
        {(pb.documents_required || []).map((d, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-teal-600 flex-shrink-0" />
            <span>{d}</span>
          </li>
        ))}
      </ul>

      {/* Citations */}
      <SectionHeading>What to cite</SectionHeading>
      <ul className="space-y-2 text-[15px] text-slate-800 leading-[1.6]">
        {(pb.citation_framework || []).map((c, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-teal-600 flex-shrink-0" />
            <span>{c}</span>
          </li>
        ))}
      </ul>

      {/* Payor tactics — definition list style for scannability */}
      <SectionHeading>How to fight {payer}</SectionHeading>
      <dl className="text-[15px] text-slate-800 leading-[1.6] space-y-3">
        <div>
          <dt className="font-semibold text-slate-900 mb-0.5">Appeal process</dt>
          <dd className="text-slate-700">{pb.payor_tactics?.appeal_process || "—"}</dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-900 mb-0.5">Peer-to-peer review</dt>
          <dd className="text-slate-700">{pb.payor_tactics?.peer_to_peer || "—"}</dd>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <dt className="font-semibold text-slate-900 mb-0.5">Filing window</dt>
            <dd className="text-slate-700">
              {pb.payor_tactics?.filing_window_days
                ? `${pb.payor_tactics.filing_window_days} days from remittance`
                : "—"}
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-slate-900 mb-0.5">Portal</dt>
            <dd className="text-slate-700 break-all">{pb.payor_tactics?.portal_url || "—"}</dd>
          </div>
        </div>
        <div>
          <dt className="font-semibold text-slate-900 mb-0.5">Escalation path</dt>
          <dd className="text-slate-700">{pb.payor_tactics?.escalation_path || "—"}</dd>
        </div>
      </dl>

      {/* Common pushback */}
      {pb.common_pushback && pb.common_pushback.length > 0 && (
        <>
          <SectionHeading>Expect these payor objections</SectionHeading>
          <ul className="space-y-3 text-[15px] text-slate-800 leading-[1.6]">
            {pb.common_pushback.map((p, i) => (
              <li key={i} className="pl-4 border-l-2 border-slate-300">{p}</li>
            ))}
          </ul>
        </>
      )}

      {/* Win probability */}
      {pb.win_notes && (
        <>
          <SectionHeading>Reality check on win probability</SectionHeading>
          <p className="text-[15px] text-slate-800 leading-[1.65]">{pb.win_notes}</p>
        </>
      )}
    </div>
  );
};

const FilterSelect = ({ label, options, selected, onToggle, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid={testId}>
      <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">{label}</label>
      <button onClick={() => setOpen((o) => !o)} className="w-full border border-slate-300 rounded-md px-2 py-1.5 bg-white text-left flex items-center justify-between">
        <span className="truncate">{selected.length === 0 ? "Any" : `${selected.length} selected`}</span>
        <ChevronDown className="h-3 w-3 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg p-1">
          {options.map((opt) => (
            <button key={opt} data-testid={`${testId}-opt-${opt.replace(/\s+/g, "_")}`} onClick={() => onToggle(opt)}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50 flex items-center gap-2">
              {selected.includes(opt) ? <CheckSquare className="h-3.5 w-3.5 text-[#0F4C81]" /> : <Square className="h-3.5 w-3.5 text-slate-300" />}
              <span className="truncate">{opt}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const NumberFilter = ({ label, value, onChange, testId }) => (
  <div data-testid={testId}>
    <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">{label}</label>
    <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 rounded-md px-2 py-1.5 bg-white" placeholder="—" />
  </div>
);
